import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Divider, Menu, MenuItem, Tooltip, TooltipProps, styled, tooltipClasses } from "@mui/material";
import React, { FC, MouseEvent, ReactElement, useCallback, useContext, useEffect, useMemo } from 'react';
import { AppSettings } from '@/AppSettings';
import { useSessionStorage } from 'react-use';
import { grey } from '@mui/material/colors';
import { Note, NoteStatus, NoteType } from '@/api/dtos';
import { ITrialsContext } from '@/contexts/data/TrialsContext';
import { IMasterGroupsContext } from '@/contexts/data/MasterGroupsContext';
import { ICoordinatingGroupContext } from '@/contexts/data/CoordinatingGroupContext';
import { IInstitutionsContext } from '@/contexts/data/InstitutionsContext';
import { IParticipantsContext } from '@/contexts/data/ParticipantsContext';
import { ICoordinatingGroupsContext } from '@/contexts/data/CoordinatingGroupsContext';
import noteTypeToText from '@/utilities/configuration/noteTypeToText';
import noteStatusToText from '@/utilities/configuration/noteStatusToText';
import { ISearchField } from '@/components/dialogs/SearchDialog';
import Select from '@/components/forms/inputs/Select';
import ExternalUsersContext from '@/contexts/data/ExternalUsersContext';

export const NotesAndQueriesListSortTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        minWidth: theme.spacing(10),
        fontSize: '1rem',
        fontWeight: 'normal',
        padding: theme.spacing(2),
        backgroundColor: grey[700]
    },
}));

const StyledMenuItem = styled(MenuItem)(({ theme}) => ({
    gap: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
}));

const StyledDivider = styled(Divider)(({ theme}) => ({
    margin: `0 !important`
}));

export interface INotesAndQueriesListSortableContexts {
    trials: ITrialsContext['idMap'];
    masterGroups: IMasterGroupsContext['idMap'];
    coordinatingGroups: ICoordinatingGroupsContext['idMap'];
    institutions: IInstitutionsContext['idMap'];
    participants: IParticipantsContext['idMap'];
}

export interface INotesAndQueriesListSortable {
    sort: (itemA: Note, itemB: Note, context: INotesAndQueriesListSortableContexts) => -1 | 0 | 1;
    name: string;
}

export interface INotesAndQueriesListSortProps {
    sortKey: string;
    sortableFields?: INotesAndQueriesListSortable[],
    onChange: (sort: {field: INotesAndQueriesListSortable; direction: 'asc' | 'desc'}) => void;
}

export interface INotesAndQueriesListFilterProps {
    selectedType?: NoteType;
    selectedStatus?: NoteStatus;
    selectedUsername?: string;
    types: Record<NoteType, number>;
    statuses: Record<NoteStatus, number>;
    onChange: (type?: NoteType, status?: NoteStatus, username?: string) => void;
}

export const NotesAndQueriesListFilter: FC<INotesAndQueriesListFilterProps> = ({
    selectedStatus,
    selectedType,
    selectedUsername,
    onChange
}) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const { data: users } = useContext(ExternalUsersContext);
    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const onTypeSelected = useCallback((type?: NoteType) => {
        handleClose();

        let newStatus = selectedStatus;

        if (type === NoteType.Annotation || 
            type === NoteType.ProtocolDeviation ||
            type === NoteType.ImportantProtocolDeviation ||
            type === NoteType.ReasonForChange ||
            selectedStatus === NoteStatus.NotApplicable) {
                newStatus = undefined;
            }

        onChange(type, newStatus, selectedUsername);
    }, [onChange, selectedStatus, selectedUsername, handleClose]);

    const onStatusSelected = useCallback((status?: NoteStatus) => {
        handleClose();

        onChange(selectedType, status, selectedUsername);
    }, [onChange, selectedType, selectedUsername, handleClose])

    const onUsernameSelected = useCallback((username?: string) => {
        handleClose();

        onChange(selectedType, selectedStatus, username);
    }, [onChange, selectedType, selectedStatus, handleClose]);
    
    const selectedUser = useMemo(() => {
        if(selectedUsername === "All") {
            return undefined;
        }

        return users?.find(x => x.username === selectedUsername);
    }, [users, selectedUsername]);

    return (
        <>
            <NotesAndQueriesListSortTooltip
                placement="top"
                title={
                    <>
                        <strong>Type</strong>
                        <Box>
                            {
                                selectedType ?
                                    noteTypeToText(selectedType) :
                                    'All'
                            }
                        </Box>
                    </>
                }
            >
                <Button
                    id="btn-type"
                    color="inherit"
                    aria-label="type selection"
                    aria-controls="menu-type"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth  style={{fontSize: '1rem'}} />}
                >
                    {
                        selectedType ?
                            noteTypeToText(selectedType) :
                            'All'
                    }
                </Button>
            </NotesAndQueriesListSortTooltip>
            <Menu
                id="menu-type"
                anchorEl={anchorEl}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={anchorEl?.id === 'btn-type'}
                onClose={handleClose}
                MenuListProps={{
                    disablePadding: true
                }}
            >
                
                <StyledMenuItem 
                    onClick={() => onTypeSelected()}
                >
                    All
                </StyledMenuItem>
                {
                    AppSettings.noteTypes[NoteType.Annotation] && (
                            <StyledDivider />
                    )
                }
                {
                    AppSettings.noteTypes[NoteType.Annotation] && (
                        <StyledMenuItem 
                            onClick={() => onTypeSelected(NoteType.Annotation)}
                        >
                            {noteTypeToText(NoteType.Annotation)}
                        </StyledMenuItem>
                    )
                        
                }
                {
                    AppSettings.noteTypes[NoteType.InternalQuery] && (
                            <StyledDivider />
                    )
                }
                {
                    AppSettings.noteTypes[NoteType.InternalQuery] && (
                        <StyledMenuItem 
                            onClick={() => onTypeSelected(NoteType.InternalQuery)}
                        >
                            {noteTypeToText(NoteType.InternalQuery)}
                        </StyledMenuItem>
                    )
                        
                }
                {
                    AppSettings.noteTypes[NoteType.ImportantProtocolDeviation] && (
                            <StyledDivider />
                    )
                }
                {
                    AppSettings.noteTypes[NoteType.ImportantProtocolDeviation] && (
                        <StyledMenuItem
                            onClick={() => onTypeSelected(NoteType.ImportantProtocolDeviation)}
                        >
                            {noteTypeToText(NoteType.ImportantProtocolDeviation)}
                        </StyledMenuItem>
                    )
                        
                }
                {
                    AppSettings.noteTypes[NoteType.ProtocolDeviation] && (
                            <StyledDivider />
                    )
                }
                {
                    AppSettings.noteTypes[NoteType.ProtocolDeviation] && (
                        <StyledMenuItem 
                            onClick={() => onTypeSelected(NoteType.ProtocolDeviation)}
                        >
                            {noteTypeToText(NoteType.ProtocolDeviation)}
                        </StyledMenuItem>
                    )
                        
                }
                {
                    AppSettings.noteTypes[NoteType.ReasonForChange] && (
                            <StyledDivider />
                    )
                }
                {
                    AppSettings.noteTypes[NoteType.ReasonForChange] && (
                        <StyledMenuItem 
                            onClick={() => onTypeSelected(NoteType.ReasonForChange)}
                        >
                            {noteTypeToText(NoteType.ReasonForChange)}
                        </StyledMenuItem>
                    )
                        
                }
                {
                    AppSettings.noteTypes[NoteType.SiteQuery] && (
                            <StyledDivider />
                    )
                }
                {
                    AppSettings.noteTypes[NoteType.SiteQuery] && (
                        <StyledMenuItem 
                            onClick={() => onTypeSelected(NoteType.SiteQuery)}
                        >
                            {noteTypeToText(NoteType.SiteQuery)}
                        </StyledMenuItem>
                    )
                        
                }
                {
                    AppSettings.noteTypes[NoteType.System] && (
                            <StyledDivider />
                    )
                }
                {
                    AppSettings.noteTypes[NoteType.System] && (
                        <StyledMenuItem 
                            onClick={() => onTypeSelected(NoteType.System)}
                        >
                            {noteTypeToText(NoteType.System)}
                        </StyledMenuItem>
                    )
                        
                }
            </Menu>
            {
                selectedType !== NoteType.Annotation &&
                selectedType !== NoteType.ProtocolDeviation &&
                selectedType !== NoteType.ImportantProtocolDeviation &&
                selectedType !== NoteType.ReasonForChange && (
                    <>
                        <NotesAndQueriesListSortTooltip
                            placement="top"
                            title={
                                <>
                                    <strong>Status</strong>
                                    <Box>
                                        {
                                            selectedStatus ?
                                                noteStatusToText(selectedStatus) :
                                                'All'
                                        }
                                    </Box>
                                </>
                            }
                        >
                            <Button
                                id="btn-status"
                                color="inherit"
                                aria-label="type selection"
                                aria-controls="menu-status"
                                aria-haspopup="true"
                                sx={{ml: 2}}
                                onClick={handleMenu}
                                endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth style={{ fontSize: '1rem' }} />}
                            >
                                {
                                    selectedStatus ?
                                        noteStatusToText(selectedStatus) :
                                        'All'
                                }
                            </Button>
                        </NotesAndQueriesListSortTooltip>
                        <Menu
                            id="menu-status"
                            anchorEl={anchorEl}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            open={anchorEl?.id === 'btn-status'}
                            onClose={handleClose}
                            MenuListProps={{
                                disablePadding: true
                            }}
                        >
                            
                            <StyledMenuItem 
                                onClick={() => onStatusSelected()}
                            >
                                All
                            </StyledMenuItem>
                            <StyledDivider />
                            <StyledMenuItem 
                                onClick={() => onStatusSelected(NoteStatus.New)}
                            >
                                {noteStatusToText(NoteStatus.New)}
                            </StyledMenuItem>
                            <StyledDivider />
                            <StyledMenuItem 
                                onClick={() => onStatusSelected(NoteStatus.Updated)}
                            >
                                {noteStatusToText(NoteStatus.Updated)}
                            </StyledMenuItem>
                            <StyledDivider />
                            <StyledMenuItem 
                                onClick={() => onStatusSelected(NoteStatus.ResolutionProposed)}
                            >
                                {noteStatusToText(NoteStatus.ResolutionProposed)}
                            </StyledMenuItem>
                            <StyledDivider />
                            <StyledMenuItem 
                                onClick={() => onStatusSelected(NoteStatus.Closed)}
                            >
                                {noteStatusToText(NoteStatus.Closed)}
                            </StyledMenuItem>
                            {
                                !selectedType && (
                                    <StyledDivider />
                                )
                            }
                            {
                                !selectedType && (
                                    <StyledMenuItem 
                                        onClick={() => onStatusSelected(NoteStatus.NotApplicable)}
                                    >
                                        {noteStatusToText(NoteStatus.NotApplicable)}
                                    </StyledMenuItem>
                                )
                            }
                        </Menu>
                    </>
                )
            }
            {
                users && users.length > 0 &&
                <>
                    <NotesAndQueriesListSortTooltip
                        placement="top"
                        title={
                            <>
                                <strong>User</strong>
                                <Box>
                                    {
                                        selectedUser
                                            ?
                                            selectedUser.firstName || selectedUser.lastName ?
                                                <>{selectedUser.username} ({selectedUser.firstName} {selectedUser.lastName})</> :
                                                <>{selectedUser.username}</> 
                                            :"All"
                                    }
                                </Box>
                            </>
                        }
                    >
                        <Button
                            id="btn-user"
                            color="inherit"
                            aria-label="user selection"
                            aria-controls="menu-user"
                            aria-haspopup="true"
                            sx={{ml: 2}}
                            onClick={handleMenu}
                            endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth  style={{fontSize: '1rem'}} />}
                        >
                            {
                                selectedUser
                                    ?
                                    selectedUser.firstName || selectedUser.lastName ?
                                        <>{selectedUser.username} ({selectedUser.firstName} {selectedUser.lastName})</> :
                                        <>{selectedUser.username}</> 
                                    :"All"
                            }
                        </Button>
                    </NotesAndQueriesListSortTooltip>
                    <Menu
                        id="menu-user"
                        anchorEl={anchorEl}
                        keepMounted
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        open={anchorEl?.id === 'btn-user'}
                        onClose={handleClose}
                        MenuListProps={{
                            disablePadding: true
                        }}
                    >
                        
                        <StyledMenuItem 
                            onClick={() => onUsernameSelected()}
                        >
                            All
                        </StyledMenuItem>
                        {
                            users.flatMap(user => [
                                    <StyledDivider key={`divider-${user.username}`} />,
                                    <StyledMenuItem 
                                        key={`menu-item-${user.username}`}
                                        onClick={() => onUsernameSelected(user.username)}
                                    >
                                        {user.username}
                                    </StyledMenuItem>
                            ])
                        }
                    </Menu>
                </>
            }
        </>
    );
}

const NotesAndQueriesListSort: FC<INotesAndQueriesListSortProps> = ({
    sortableFields,
    sortKey,
    onChange
}) => {
    const [sortName, setSortName] = useSessionStorage<string | undefined>(`${sortKey}-sort`, undefined);
    const [sortDirection, setSortDirection] = useSessionStorage<'asc' | 'desc' | undefined>(`${sortKey}-sort-direction`, 'asc')
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);


    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const onSortSelected = useCallback((field: INotesAndQueriesListSortable, direction: 'asc' | 'desc') => {
        setSortName(field.name);
        setSortDirection(direction);
        handleClose();

        onChange({ field, direction });
    }, [setSortDirection, setSortName, handleClose])

    useEffect(() => {
        if (!sortableFields || sortableFields.length == 0) {
            return;
        }

        let field = sortableFields.find(x => x.name === sortName) ?? sortableFields[0];

        //if (sortName !== field.name) {
            onSortSelected(field, sortDirection ?? 'asc');
        //}
    }, [sortableFields, onSortSelected, sortName, sortDirection]);

    const onFieldSelected = useCallback((field: INotesAndQueriesListSortable) => {
        setSortName(field.name);
        handleClose();

        if (sortDirection) {
            onChange({ field, direction: sortDirection });
        }
    }, [setSortName, handleClose, sortableFields, sortDirection])

    const onDirectionSelected = useCallback((direction: 'asc' | 'desc') => {
        setSortDirection(direction);
        handleClose();

        let field = sortableFields?.find(x => x.name === sortName) ?? sortableFields?.[0];

        if (field) {
            onChange({ field, direction });
        }
    }, [setSortDirection, handleClose, sortableFields, sortName])

    if (!sortableFields || sortableFields.length == 0) {
        return null;
    }

    return (
        <>
            <NotesAndQueriesListSortTooltip
                placement="top"
                title={
                    <>
                        <strong>Sort Field</strong>
                        <Box>
                            {sortName}
                        </Box>
                    </>
                }
            >
                <Button
                    id="btn-field"
                    color="inherit"
                    aria-label="institution selection"
                    aria-controls="menu-field"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth  style={{fontSize: '1rem'}} />}
                >
                    {sortName}
                </Button>
            </NotesAndQueriesListSortTooltip>
            <Menu
                id="menu-field"
                anchorEl={anchorEl}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={anchorEl?.id === 'btn-field'}
                onClose={handleClose}
                MenuListProps={{
                    disablePadding: true
                }}
            >
                {
                    sortableFields?.map((x, i, a) => {
                        return (
                            [
                                <StyledMenuItem key={x.name} onClick={() => onFieldSelected(x)}>{x.name}</StyledMenuItem>,
                                i < (a.length - 1) ? <StyledDivider key={`${x.name}-divider`} /> : null
                            ]
                        );
                    })
                }
            </Menu>
            <NotesAndQueriesListSortTooltip
                placement="top"
                title={
                    <>
                        <strong>Sort Direction</strong>
                        {
                            sortDirection === 'desc' ? 
                                <Box>
                                    Descending <FontAwesomeIcon icon={AppSettings.icons.descending} fixedWidth />
                                </Box> :
                                <Box> 
                                    Ascending <FontAwesomeIcon icon={AppSettings.icons.ascending} fixedWidth />
                                </Box>
                        }
                    </>
                }
            >
                <Button
                    id="btn-direction"
                    color="inherit"
                    aria-label="institution selection"
                    aria-controls="menu-direction"
                    aria-haspopup="true"
                    sx={{ml: 2}}
                    onClick={handleMenu}
                    endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth  style={{fontSize: '1rem'}} />}
                >
                    {sortDirection === 'desc' ? <FontAwesomeIcon icon={AppSettings.icons.descending} fixedWidth /> : <FontAwesomeIcon icon={AppSettings.icons.ascending} fixedWidth />}
                </Button>
            </NotesAndQueriesListSortTooltip>
            <Menu
                id="menu-direction"
                anchorEl={anchorEl}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={anchorEl?.id === 'btn-direction'}
                onClose={handleClose}
                MenuListProps={{
                    disablePadding: true
                }}
            >
                <StyledMenuItem onClick={() => onDirectionSelected('asc')}>Ascending <FontAwesomeIcon icon={AppSettings.icons.ascending} fixedWidth /></StyledMenuItem>
                <StyledDivider />
                <StyledMenuItem onClick={() => onDirectionSelected('desc')}>Descending  <FontAwesomeIcon icon={AppSettings.icons.descending} fixedWidth /></StyledMenuItem>
            </Menu>
        </>
    );
}



export const DefaultSortableFields: Record<string, INotesAndQueriesListSortable> = {
    trial: {
        name: 'Trial',
        sort: (a, b, context) => {
            const itemA = a.trialId ? context.trials?.[a.trialId] : undefined; 
            const itemB = b.trialId ? context.trials?.[b.trialId] : undefined;
            
            const valueA = itemA?.name ?? '';
            const valueB = itemB?.name ?? '';

            return valueA < valueB ? -1 : 1
        }
    },
    masterGroup: {
        name: 'Master Group',
        sort: (a, b, context) => {
            const itemA = a.masterGroupId ? context.masterGroups?.[a.masterGroupId] : undefined; 
            const itemB = b.masterGroupId ? context.masterGroups?.[b.masterGroupId] : undefined;
            
            const valueA = itemA?.name ?? '';
            const valueB = itemB?.name ?? '';

            return valueA < valueB ? -1 : 1
        }
    },
    coordinatingGroup: {
        name: 'Coordinating Group',
        sort: (a, b, context) => {
            const itemA = a.coordinatingGroupId ? context.coordinatingGroups?.[a.coordinatingGroupId] : undefined; 
            const itemB = b.coordinatingGroupId ? context.coordinatingGroups?.[b.coordinatingGroupId] : undefined;
            
            const valueA = itemA?.name ?? '';
            const valueB = itemB?.name ?? '';

            return valueA < valueB ? -1 : 1
        }
    },
    institution: {
        name: 'Institution',
        sort: (a, b, context) => {
            const itemA = a.institutionId ? context.institutions?.[a.institutionId] : undefined; 
            const itemB = b.institutionId ? context.institutions?.[b.institutionId] : undefined;
            
            const valueA = itemA?.name ?? '';
            const valueB = itemB?.name ?? '';

            return valueA < valueB ? -1 : 1
        }
    },
    participant: {
        name: 'Participant',
        sort: (a, b, context) => {
            const itemA = a.participantId ? context.participants?.[a.participantId] : undefined; 
            const itemB = b.participantId ? context.participants?.[b.participantId] : undefined;
            
            const valueA = itemA?.studyNumber ?? '';
            const valueB = itemB?.studyNumber ?? '';

            return valueA < valueB ? -1 : 1
        }
    },
    dateCreated: {
        name: 'Date Entered',
        sort: (a, b, context) => a.createdDate! < b.createdDate! ? -1 : 1
    },
    dateUpdated: {
        name: 'Date Updated',
        sort: (a, b, context) => a.createdDate! < b.createdDate! ? -1 : 1
    },
    type: {
        name: 'Type',
        sort: (a, b, context) => noteTypeToText(a.type!) < noteTypeToText(b.type!) ? -1 : 1
    },
    status: {
        name: 'Status',
        sort: (a, b, context) => noteStatusToText(a.status!) < noteStatusToText(b.status!) ? -1 : 1
    }
}


export const DefaultSearchableFields: Record<string, (context: INotesAndQueriesListSortableContexts) => ISearchField<any>> = {
    trial: (context) => ({
        key: 'trialId',
        label: 'Trial',
        component: Select,
        componentProps: {
            options: context.trials ? 
                Object
                    .keys(context.trials)
                    .map(x => {
                        const item = context.trials?.[x as any];

                        return {
                            id: item?.id,
                            name: item?.name 
                        }
                    }) :
                undefined,
            FormControlProps: {
                margin: 'normal'
            }
        }
    }),
    masterGroup: (context) => ({
        key: 'masterGroupId',
        label: 'Master Group',
        component: Select,
        componentProps: {
            options: context.masterGroups ? 
                Object
                    .keys(context.masterGroups)
                    .map(x => {
                        const item = context.masterGroups?.[x as any];

                        return {
                            id: item?.id,
                            name: item?.name 
                        }
                    }) :
                undefined,
            FormControlProps: {
                margin: 'normal'
            }
        }
    }),
    coordinatingGroup: (context) => ({
        key: 'coordinatingGroupId',
        label: 'Coordinating Group',
        component: Select,
        componentProps: {
            options: context.coordinatingGroups ? 
                Object
                    .keys(context.coordinatingGroups)
                    .map(x => {
                        const item = context.coordinatingGroups?.[x as any];

                        return {
                            id: item?.id,
                            name: item?.name 
                        }
                    }) :
                undefined,
            FormControlProps: {
                margin: 'normal'
            }
        }
    }),
    institution: (context) => ({
        key: 'institutionId',
        label: 'Institution',
        component: Select,
        componentProps: {
            options: context.institutions ? 
                Object
                    .keys(context.institutions)
                    .map(x => {
                        const item = context.institutions?.[x as any];

                        return {
                            id: item?.id,
                            name: item?.name 
                        }
                    }) :
                undefined,
            FormControlProps: {
                margin: 'normal'
            }
        }
    }),
    participant: (context) => ({
        key: 'participantId',
        label: 'Participant',
        component: Select,
        componentProps: {
            options: context.participants ? 
                Object
                    .keys(context.participants)
                    .map(x => {
                        const item = context.participants?.[x as any];

                        return {
                            id: item?.id,
                            name: item?.studyNumber 
                        }
                    }) :
                undefined,
            FormControlProps: {
                margin: 'normal'
            }
        }
    }),
    type: (context) => ({
        key: 'type',
        label: 'Type',
        component: Select,
        componentProps: {
            options: [
                {
                    id: NoteType.Annotation,
                    name: noteTypeToText(NoteType.Annotation)
                },
                {
                    id: NoteType.InternalQuery,
                    name: noteTypeToText(NoteType.InternalQuery)
                },
                {
                    id: NoteType.ProtocolDeviation,
                    name: noteTypeToText(NoteType.ProtocolDeviation)
                },
                {
                    id: NoteType.ImportantProtocolDeviation,
                    name: noteTypeToText(NoteType.ImportantProtocolDeviation)
                },
                {
                    id: NoteType.ReasonForChange,
                    name: noteTypeToText(NoteType.ReasonForChange)
                },
                {
                    id: NoteType.SiteQuery,
                    name: noteTypeToText(NoteType.SiteQuery)
                },
                {
                    id: NoteType.System,
                    name: noteTypeToText(NoteType.System)
                }
            ],
            FormControlProps: {
                margin: 'normal'
            }
        }
    }),
    status: (context) => ({
        key: 'status',
        label: 'Status',
        component: Select,
        componentProps: {
            options: [
                {
                    id: NoteStatus.Closed,
                    name: noteStatusToText(NoteStatus.Closed)
                },
                {
                    id: NoteStatus.New,
                    name: noteStatusToText(NoteStatus.New)
                },
                {
                    id: NoteStatus.NotApplicable,
                    name: noteStatusToText(NoteStatus.NotApplicable)
                },
                {
                    id: NoteStatus.ResolutionProposed,
                    name: noteStatusToText(NoteStatus.ResolutionProposed)
                },
                {
                    id: NoteStatus.Updated,
                    name: noteStatusToText(NoteStatus.Updated)
                },
            ],
            FormControlProps: {
                margin: 'normal'
            }
        }
    }),
}

export default NotesAndQueriesListSort;